import { createContext, useMemo, useRef, useState } from 'react';
import { useAsyncState } from '@hilma/tools';
import { Area, FullStation, Localize, SelectArea, useAreas, useTransform, useTransformObject } from '@hilma/netina2all';

import { StationsParams } from '../../types';

interface StationSelectionProviderValue {
    selectedStationId: number | null;
    setSelectedStationId: (value: React.SetStateAction<number | null>, callback?: ((state: number | null) => void) | undefined) => Promise<number | null>;
    getSelectedStationId: () => Promise<number | null>;
    selectedStation: FullStation | null;
    setSelectedStation: React.Dispatch<React.SetStateAction<FullStation | null>>;
    stations: Localize<FullStation>[];
    setStations: React.Dispatch<React.SetStateAction<Localize<FullStation>[]>>;
    resNum: number;
    setResNum: React.Dispatch<React.SetStateAction<number>>;
    selectedMapStationId: number | null;
    setSelectedMapStationId: React.Dispatch<React.SetStateAction<number | null>>;
    mapOptions: google.maps.MapOptions;
    setMapOptions: React.Dispatch<React.SetStateAction<google.maps.MapOptions>>;
    isSelectedStationOpen: boolean;
    setIsSelectedStationOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isMapOpen: boolean;
    setIsMapOpen: React.Dispatch<React.SetStateAction<boolean>>;
    translatedAreas: SelectArea[];
    translatedStations: FullStation[];
    stationsParams: React.MutableRefObject<StationsParams>;
}

export const StationSelectionContext = createContext<StationSelectionProviderValue | null>(null)

export const StationSelectionProvider: React.FC = ({ children }) => {

    const transformObject = useTransformObject()
    const areas = useAreas();

    const [selectedStationId, setSelectedStationId, getSelectedStationId] = useAsyncState<number | null>(null)
    const [selectedStation, setSelectedStation] = useState<FullStation | null>(null)
    const [stations, setStations] = useState<Localize<FullStation>[]>([])
    const [resNum, setResNum] = useState(0)
    const [selectedMapStationId, setSelectedMapStationId] = useState<number | null>(null)
    const [mapOptions, setMapOptions] = useState<google.maps.MapOptions>({})
    const [isSelectedStationOpen, setIsSelectedStationOpen] = useState(false)
    const [isMapOpen, setIsMapOpen] = useState(false)

    const stationsParams = useRef<StationsParams>({ offset: 0, limit: 10 })

    const translatedAreas = useMemo(() => {
        if (!areas) return [];

        return areas.map((item) => {
            const area: Area | SelectArea = transformObject(item) as any;
            (area as SelectArea).name = (area as Area).areaName;
            delete (area as Partial<Area>).areaName;
            return area as SelectArea;
        });
    }, [transformObject, areas]);

    const translatedStations = useTransform<FullStation>(stations);

    return (
        <StationSelectionContext.Provider value={{
            selectedStationId, setSelectedStationId, getSelectedStationId,
            selectedStation, setSelectedStation,
            stations, setStations,
            resNum, setResNum,
            selectedMapStationId, setSelectedMapStationId,
            mapOptions, setMapOptions,
            isSelectedStationOpen, setIsSelectedStationOpen,
            isMapOpen, setIsMapOpen,
            translatedAreas,
            translatedStations,
            stationsParams
        }}>
            {children}
        </StationSelectionContext.Provider>
    )
}